import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLLAGE_LAYERS_BY_VARIANT } from './landingCollageConfig';
import { useCollageMotion } from '../../hooks/useCollageMotion';

/**
 * کلاژ چندتصویری برای سکشن‌های لندینگ (ورود در اسcroll، پارالاکس ماوس، شناوری CSS).
 *
 * @param {{ variant: keyof typeof COLLAGE_LAYERS_BY_VARIANT, embedded?: boolean }} props
 */
function LandingCollageScene({ variant, embedded = false }) {
  const { t } = useTranslation();
  const layers = COLLAGE_LAYERS_BY_VARIANT[variant] ?? COLLAGE_LAYERS_BY_VARIANT.process;
  const { containerRef, inView, getParallaxStyle, reducedMotion } = useCollageMotion();
  const show = reducedMotion || inView;

  const ariaLabel = t(`landingCollage.${variant}Aria`);

  const shellClass = embedded
    ? 'relative aspect-[5/4] w-full min-h-[220px] overflow-hidden bg-sky-wash/50 md:aspect-[4/3] md:min-h-[300px] lg:min-h-[340px]'
    : 'relative aspect-[5/4] min-h-[260px] w-full overflow-hidden rounded-2xl bg-hero-wash shadow-saas ring-1 ring-signal/[0.08] transition duration-500 ease-out motion-reduce:transition-none md:aspect-[4/3] md:min-h-[300px] md:hover:scale-[1.02] motion-reduce:md:hover:scale-100 hover:shadow-saas-glow';

  return (
    <div ref={containerRef} role="img" aria-label={ariaLabel} className={shellClass}>
      <div className="pointer-events-none absolute -start-[12%] -top-[18%] h-[50%] w-[48%] rounded-full bg-sky-bloom/30 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-[20%] -end-[10%] h-[44%] w-[40%] rounded-full bg-signal/10 blur-3xl" aria-hidden />

      {/* Stage inset — artwork fills frame with even padding on all sides */}
      <div className="absolute inset-[4%] sm:inset-[5%] md:inset-[6%]">
        <div
          className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-tr from-signal/[0.03] via-transparent to-sky-bloom/[0.06]"
          aria-hidden
        />

        {layers.map((layer, index) => {
          const floatClass =
            layer.float === 'none'
              ? ''
              : layer.float === 'b'
                ? 'motion-safe:animate-collage-float-b motion-reduce:animate-none'
                : 'motion-safe:animate-collage-float-a motion-reduce:animate-none';

          return (
            <div
              key={layer.src}
              style={{
                top: layer.top,
                insetInlineStart: layer.start,
                width: layer.width,
                zIndex: layer.zIndex,
                transitionDelay: reducedMotion ? '0ms' : `${layer.staggerMs}ms`,
              }}
              className={`absolute max-w-none will-change-transform ${
                layer.hideBelowMd ? 'hidden md:block' : ''
              } transition-[opacity,transform] duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity motion-reduce:duration-500 ${
                show
                  ? 'translate-y-0 scale-100 opacity-100 motion-reduce:translate-y-0'
                  : '-translate-y-6 scale-[0.94] opacity-0'
              }`}
            >
              <div
                style={layer.float === 'none' ? undefined : { animationDelay: `${index * 0.45}s` }}
                className={floatClass}
              >
                <div style={getParallaxStyle(layer.depth)} className="h-full w-full">
                  <img
                    src={layer.src}
                    alt=""
                    width={352}
                    height={264}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="h-auto w-full select-none object-contain object-bottom drop-shadow-[0_12px_24px_rgba(113,75,103,0.12)]"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LandingCollageScene;
